import {
  useChangeReportingPlanMutation,
  useCreateReportingCheckoutSessionMutation,
  useReportingBillingPortalMutation,
} from '@cast/shared-trial-data-access';
import { notify } from '@cast/design-system';

import { raiseReportingMonetizationEvent } from './analytics/reportingMonetizationEvents';
import { reportingPlanIdToTier, type ReportingPlanId } from './reportingPlans';

/**
 * Payload when the user starts paid checkout or changes plan from Plans.
 */
export type ReportingCheckoutRequest = {
  planId: ReportingPlanId;
  planName: string;
  vcpuCapLabel: string;
  priceLabel: string;
};

export type UseReportingCheckoutActionsOptions = {
  /**
   * When true, paid plan CTAs call change-plan (returns Stripe portal URL for
   * confirmation / proration) instead of creating a new Checkout session.
   */
  isPaid: boolean;
};

/**
 * Checkout / change-plan / billing-portal actions for reporting monetization.
 * Paid plan switches use change-plan → Stripe confirmation URL.
 */
export const useReportingCheckoutActions = ({
  isPaid,
}: UseReportingCheckoutActionsOptions) => {
  const checkoutMutation = useCreateReportingCheckoutSessionMutation();
  const changePlanMutation = useChangeReportingPlanMutation();
  const billingPortalMutation = useReportingBillingPortalMutation();

  const selectPaidPlan = (request: ReportingCheckoutRequest) => {
    const tier = reportingPlanIdToTier(request.planId);
    if (!tier) {
      notify.error('This plan cannot be purchased online.');
      return;
    }

    raiseReportingMonetizationEvent('reporting_checkout_started', {
      source: isPaid ? 'change_plan' : 'checkout_session',
      selected_plan: request.planId,
      selected_bracket: request.vcpuCapLabel,
      displayed_price: request.priceLabel,
    });

    if (isPaid) {
      changePlanMutation.mutate(tier);
      return;
    }

    checkoutMutation.mutate(tier);
  };

  const openBillingPortal = () => {
    billingPortalMutation.mutate();
  };

  return {
    selectPaidPlan,
    openBillingPortal,
    isSelectingPlan:
      checkoutMutation.isPending ||
      changePlanMutation.isPending ||
      billingPortalMutation.isPending,
    isOpeningBillingPortal: billingPortalMutation.isPending,
  };
};

/** @deprecated Prefer useReportingCheckoutActions — kept for call-site migration. */
export const notifyReportingCheckoutUnavailable = (
  _request?: ReportingCheckoutRequest
): void => {
  notify.info(
    'Stripe Checkout is not available yet. After payment, status will update via webhook — no convert API from the console.'
  );
};

/** @deprecated Prefer useReportingCheckoutActions — kept for call-site migration. */
export const notifyReportingBillingPortalUnavailable = (): void => {
  notify.info('Stripe billing portal is not available yet.');
};
